// Zod schemas for request validation — used by api routes and frontend forms.
// Note: entity-level Zod schemas (insert/update) are auto-generated from Drizzle
// via drizzle-zod in packages/db. These are request-specific shapes.

import { z } from 'zod';

// ─── Auth ───────────────────────────────────────────────────────────────────
export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(8).max(128),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const registerSchema = z.object({
  name: z.string().min(2).max(100),
  email: z.string().email(),
  password: z.string().min(8).max(128),
  phone: z.string().regex(/^[0-9+()\-\s]{7,20}$/).optional(),
  promoCode: z.string().max(50).optional(),
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const mobileRefreshSchema = z.object({
  refreshToken: z.string().min(48),
});
export type MobileRefreshInput = z.infer<typeof mobileRefreshSchema>;

export const passwordResetRequestSchema = z.object({
  email: z.string().email(),
});
export type PasswordResetRequestInput = z.infer<typeof passwordResetRequestSchema>;

export const passwordResetConfirmSchema = z.object({
  token: z.string().min(32),
  password: z.string().min(8).max(128),
});
export type PasswordResetConfirmInput = z.infer<typeof passwordResetConfirmSchema>;

// ─── Admin: users ───────────────────────────────────────────────────────────
export const adminCreateUserSchema = z.object({
  name: z.string().min(2).max(100),
  email: z.string().email(),
  password: z.string().min(8).max(128),
  idRol: z.string().uuid(),
  phone: z.string().optional(),
  idPlan: z.string().uuid().optional(),
  sendEmail: z.boolean().optional().default(false),
  status: z.enum(['active', 'blocked']).optional().default('active'),
});
export type AdminCreateUserInput = z.infer<typeof adminCreateUserSchema>;

export const adminUpdateUserSchema = z.object({
  name: z.string().min(2).max(100).optional(),
  email: z.string().email().optional(),
  password: z.string().min(8).max(128).optional(),
  idRol: z.string().uuid().optional(),
  status: z.enum(['active', 'blocked', 'deleted']).optional(),
  phone: z.string().optional(),
  idPlan: z.string().uuid().optional(),
});
export type AdminUpdateUserInput = z.infer<typeof adminUpdateUserSchema>;

// ─── Admin: roles ───────────────────────────────────────────────────────────
export const createRoleSchema = z.object({
  name: z.string().min(2).max(60),
  slug: z.string().min(2).max(60).regex(/^[a-z0-9-]+$/),
  description: z.string().max(255).optional(),
});
export type CreateRoleInput = z.infer<typeof createRoleSchema>;

export const updateRolePermissionsSchema = z.object({
  routeIds: z.array(z.string().uuid()),
});
export type UpdateRolePermissionsInput = z.infer<typeof updateRolePermissionsSchema>;

// ─── Admin: routes metadata ─────────────────────────────────────────────────
export const updateRouteMetaSchema = z.object({
  seoTitle: z.string().max(200).nullable().optional(),
  seoDescription: z.string().max(500).nullable().optional(),
  robots: z.string().max(60).optional(),
  menuLabel: z.string().max(100).nullable().optional(),
  requireAuth: z.boolean().optional(),
  requirePlan: z.boolean().optional(),
  redirectIfAuthed: z.boolean().optional(),
  isAvailable: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
});
export type UpdateRouteMetaInput = z.infer<typeof updateRouteMetaSchema>;

// ─── Admin: plans ───────────────────────────────────────────────────────────
export const createPlanSchema = z.object({
  name: z.string().min(2).max(100),
  slug: z.string().min(2).max(60).regex(/^[a-z0-9-]+$/),
  planType: z.enum(['web', 'android', 'ios']),
  price: z.string().regex(/^\d+(\.\d{1,2})?$/),
  tax: z.string().regex(/^\d+(\.\d{1,2})?$/).optional().default('0'),
  currency: z.string().length(3).default('USD'),
  storeProductIdAndroid: z.string().max(200).nullable().optional(),
  storeBasePlanIdAndroid: z.string().max(200).nullable().optional(),
  storeProductIdIos: z.string().max(200).nullable().optional(),
  isStoreSubscription: z.boolean().default(false),
  description: z.string().max(1000).nullable().optional(),
  paymentGateway: z.enum(['epayco', 'stripe']).nullable().optional(),
  // Stripe / web payment configuration
  paymentType: z.enum(['one_time', 'subscription']).nullable().optional(),
  stripePriceId: z.string().max(200).nullable().optional(),
  stripeProductId: z.string().max(200).nullable().optional(),
  logoUrl: z.string().max(500).nullable().optional(),
  billingInterval: z.enum(['month', 'year']).nullable().optional(),
  trialDays: z.number().int().min(0).max(365).optional().default(0),
});
export type CreatePlanInput = z.infer<typeof createPlanSchema>;

export const updatePlanSchema = createPlanSchema.partial().omit({ slug: true });
export type UpdatePlanInput = z.infer<typeof updatePlanSchema>;

export const updatePlanFeaturesSchema = z.object({
  features: z.array(z.object({
    idFeatureConfig: z.string().uuid(),
    allowance: z.number().int().min(0),
    unlimited: z.boolean(),
  })),
});
export type UpdatePlanFeaturesInput = z.infer<typeof updatePlanFeaturesSchema>;

// ─── Admin: tasks ───────────────────────────────────────────────────────────
const taskBaseSchema = z.object({
  tag: z.string().min(2).max(80).regex(/^[a-z0-9-]+$/),
  description: z.string().max(255).nullable().optional(),
  scheduleType: z.enum(['interval', 'daily']),
  intervalSeconds: z.number().int().positive().nullable().optional(),
  scheduleAt: z.string().regex(/^\d{2}:\d{2}$/).nullable().optional(),
  maxRetries: z.number().int().min(0).max(10).default(3),
});
export const createTaskSchema = taskBaseSchema.refine(
  (d) => (d.scheduleType === 'interval' && d.intervalSeconds) || (d.scheduleType === 'daily' && d.scheduleAt),
  { message: 'intervalSeconds required for interval type; scheduleAt (HH:MM) required for daily type' },
);
export const updateTaskSchema = taskBaseSchema.partial();
export type CreateTaskInput = z.infer<typeof createTaskSchema>;

// ─── Admin: integrations ────────────────────────────────────────────────────
export const createIntegrationSchema = z.object({
  name: z.string().min(2).max(100),
  type: z.enum(['google_analytics', 'clarity', 'recaptcha', 'google_tag', 'custom']),
  identifier: z.string().min(1).max(300),
  renderMode: z.enum(['dev', 'prod', 'both']).default('both'),
  isEnabled: z.boolean().default(true),
  template: z.string().nullable().optional(),
  routeIds: z.array(z.string().uuid()).default([]),
});
export type CreateIntegrationInput = z.infer<typeof createIntegrationSchema>;

// ─── Admin: campaigns ───────────────────────────────────────────────────────
export const createCampaignSchema = z.object({
  name: z.string().min(2).max(120),
  type: z.enum(['push', 'email']),
  audienceMode: z.enum(['all', 'roles', 'selected']),
  audienceMeta: z.object({
    roleIds: z.array(z.string().uuid()).optional(),
    userIds: z.array(z.string().uuid()).optional(),
  }).nullable().optional(),
  subject: z.string().max(300).nullable().optional(),
  bodyHtml: z.string().nullable().optional(),
  scheduledAt: z.string().datetime().nullable().optional(),
});
export type CreateCampaignInput = z.infer<typeof createCampaignSchema>;

// ─── Admin: settings ────────────────────────────────────────────────────────
export const updateSettingSchema = z.object({
  value: z.unknown(),
  category: z.string().max(12).optional(),
});
export type UpdateSettingInput = z.infer<typeof updateSettingSchema>;

export const updateOrderStatusSchema = z.object({
  status: z.enum(['pending', 'approved', 'rejected', 'refunded']),
});
export type UpdateOrderStatusInput = z.infer<typeof updateOrderStatusSchema>;

export const updateRedisConfigSchema = z.object({
  enabled: z.boolean(),
  host: z.string().max(200).optional(),
  port: z.number().int().min(1).max(65535).optional(),
  password: z.string().optional(),
  db: z.number().int().min(0).max(15).optional(),
  tls: z.boolean().optional(),
});
export type UpdateRedisConfigInput = z.infer<typeof updateRedisConfigSchema>;

export const updateS3ConfigSchema = z.object({
  bucket: z.string().max(200).optional(),
  region: z.string().max(100).optional().default('us-east-1'),
  accessKeyId: z.string().max(200).optional(),
  secretAccessKey: z.string().max(200).optional(),
  endpoint: z.string().max(300).nullable().optional(),
  forcePathStyle: z.boolean().optional().default(false),
  isPublic: z.boolean().optional().default(false),
  maxFileSizeMb: z.number().int().min(1).max(100).optional().default(5),
  allowedTypes: z.array(z.string()).optional(),
});

export const updateEmailConfigSchema = z.object({
  provider: z.enum(['resend', 'ses', 'smtp', 'sendgrid', 'mailgun']),
  fromName: z.string().max(100).optional(),
  fromEmail: z.string().email().optional(),
  resendApiKey: z.string().max(200).optional(),
  sesRegion: z.string().max(100).optional(),
  sesAccessKeyId: z.string().max(200).optional(),
  sesSecretAccessKey: z.string().max(200).optional(),
  smtpHost: z.string().max(200).optional(),
  smtpPort: z.number().int().min(1).max(65535).optional(),
  smtpUser: z.string().max(200).optional(),
  smtpPass: z.string().max(200).optional(),
  smtpSecure: z.boolean().optional(),
  sendgridApiKey: z.string().max(200).optional(),
  mailgunApiKey: z.string().max(200).optional(),
  mailgunDomain: z.string().max(200).optional(),
});

export const updateIapConfigSchema = z.object({
  appleSharedSecret: z.string().max(500).optional(),
  appleIssuerId: z.string().max(200).optional(),
  appleKeyId: z.string().max(20).optional(),
  applePrivateKey: z.string().max(5000).optional(),
  appleBundleId: z.string().max(200).optional(),
  appleEnvironment: z.enum(['sandbox', 'production']).optional().default('sandbox'),
  googlePackageName: z.string().max(200).optional(),
  googleServiceAccountJson: z.string().max(10000).optional(),
});

export const updateFcmConfigSchema = z.object({
  serviceAccountJson: z.string().max(10000).optional(),
  projectId: z.string().max(200).optional(),
});

export const updateGeminiConfigSchema = z.object({
  apiKey: z.string().max(500).optional(),
  model: z.string().max(100).optional().default('gemini-2.0-flash'),
  endpoint: z.string().max(300).optional().default('https://generativelanguage.googleapis.com/v1beta'),
});

export const updateOpenRouterConfigSchema = z.object({
  apiKey: z.string().max(500).optional(),
  model: z.string().max(100).optional().default('~openai/gpt-latest'),
  endpoint: z.string().max(300).optional().default('https://openrouter.ai/api/v1'),
});
export type UpdateOpenRouterConfigInput = z.infer<typeof updateOpenRouterConfigSchema>;

export const updateXaiConfigSchema = z.object({
  apiKey: z.string().max(500).optional(),
  model: z.string().max(100).optional().default('grok-4.6'),
  endpoint: z.string().max(300).optional().default('https://api.x.ai/v1'),
});
export type UpdateXaiConfigInput = z.infer<typeof updateXaiConfigSchema>;

// ─── Axiom Logs (drain configuration) ──────────────────────────────────────
export const updateAxiomConfigSchema = z.object({
  enabled: z.boolean().optional(),
  apiKey: z.string().max(500).optional(),
  dataset: z.string().min(1).max(200).optional(),
  endpoint: z.string().max(300).optional().default('https://api.axiom.co'),
});
export type UpdateAxiomConfigInput = z.infer<typeof updateAxiomConfigSchema>;

// ─── Admin: App Info (maintenance + versioning) ────────────────────────────
export const updateAppInfoSchema = z.object({
  statusRegister: z.boolean().optional(),
  statusLogin: z.boolean().optional(),
  statusBuy: z.boolean().optional(),
  statusApp: z.boolean().optional(),
  statusWeb: z.boolean().optional(),
});
export type UpdateAppInfoInput = z.infer<typeof updateAppInfoSchema>;

// ─── Realtime admin ────────────────────────────────────────────────────────
export const adminRealtimeBroadcastSchema = z.object({
  room: z.string().min(1).max(64).optional(),
  type: z.string().min(1).max(100),
  data: z.unknown().optional(),
});
export type AdminRealtimeBroadcastInput = z.infer<typeof adminRealtimeBroadcastSchema>;

export const adminRealtimeKickSchema = z.object({
  room: z.string().min(1).max(64).optional(),
  userId: z.string().min(1).max(64),
});
export type AdminRealtimeKickInput = z.infer<typeof adminRealtimeKickSchema>;

export const adminRealtimeRoomSchema = z.object({
  room: z.string().min(1).max(64),
});
export type AdminRealtimeRoomInput = z.infer<typeof adminRealtimeRoomSchema>;

// ─── Installer ──────────────────────────────────────────────────────────────
export const installSchema = z.object({
  appEnv: z.enum(['development', 'production']).default('development'),
  dbHost: z.string().min(1).max(200),
  dbPort: z.number().int().min(1).max(65535).default(3306),
  dbUser: z.string().min(1).max(100),
  dbPassword: z.string().max(200),
  dbName: z.string().min(1).max(100),
  adminEmail: z.string().email(),
  adminPassword: z.string().min(8).max(128),
});
export type InstallInput = z.infer<typeof installSchema>;

// ─── Pagination ─────────────────────────────────────────────────────────────
export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  perPage: z.coerce.number().int().min(1).max(200).default(20),
  search: z.string().max(100).optional(),
});
export type PaginationInput = z.infer<typeof paginationSchema>;

export interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

// ─── Mobile: Push Token Registration ───────────────────────────────────────
export const mobilePushTokenSchema = z.object({
  token: z.string().min(10).max(500),
  platform: z.enum(['iOS', 'Android']),
  deviceId: z.string().max(200).optional(),
  deviceInfo: z.object({
    model: z.string().max(100).optional(),
    os: z.string().max(50).optional(),
  }).optional(),
});
export type MobilePushTokenInput = z.infer<typeof mobilePushTokenSchema>;

// ─── Organizations ─────────────────────────────────────────────────────────
export const createOrgSchema = z.object({
  name: z.string().min(1).max(120),
  slug: z.string().min(1).max(80).regex(/^[a-z0-9-]+$/),
});

export const inviteToOrgSchema = z.object({
  email: z.string().email(),
  idRol: z.string().uuid(),
});

export const acceptInvitationSchema = z.object({
  token: z.string().min(1).max(64),
});

export const updateMemberRoleSchema = z.object({
  idRol: z.string().uuid(),
});

export const transferOrgSchema = z.object({
  newOwnerId: z.string().uuid(),
});

export const updateOrgSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  slug: z.string().min(1).max(80).regex(/^[a-z0-9-]+$/).optional(),
});

// ─── Admin: promo codes ───────────────────────────────────────────────────
export const createPromoCodeSchema = z.object({
  idPlan: z.string().uuid(),
  code: z.string().min(2).max(60).regex(/^[A-Z0-9_-]+$/i),
  percent: z.coerce.number().min(0).max(100).nullable().optional(),
  amount: z.coerce.number().min(0).nullable().optional(),
  maxUses: z.coerce.number().int().min(1).default(1),
  expiresAt: z.string().datetime().nullable().optional(),
  isAvailable: z.boolean().default(true),
});
export const updatePromoCodeSchema = z.object({
  percent: z.coerce.number().min(0).max(100).nullable().optional(),
  amount: z.coerce.number().min(0).nullable().optional(),
  maxUses: z.coerce.number().int().min(1).optional(),
  expiresAt: z.string().datetime().nullable().optional(),
  isAvailable: z.boolean().optional(),
});
export const validatePromoCodeSchema = z.object({
  code: z.string().min(1).max(60),
});
export const redeemPromoCodeSchema = z.object({
  code: z.string().min(1).max(60),
});
export type CreatePromoCodeInput = z.infer<typeof createPromoCodeSchema>;
export type UpdatePromoCodeInput = z.infer<typeof updatePromoCodeSchema>;
export type ValidatePromoCodeInput = z.infer<typeof validatePromoCodeSchema>;
export type RedeemPromoCodeInput = z.infer<typeof redeemPromoCodeSchema>;
